/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/ui/Modal.tsx
 *
 * Rôle :
 *   Modal accessible, overlay + carte centrale, animation fade+scale,
 *   respect `prefers-reduced-motion`. Utilise le `<dialog>` natif HTML5
 *   pour bénéficier gratuitement de :
 *     - focus trap natif (depuis Chrome 100+, Safari 15.4+, Firefox 98+) ;
 *     - fermeture par ESC ;
 *     - rôle ARIA `dialog` automatique ;
 *     - inertness du reste de la page (`inert` implicite).
 *
 *   Si l'utilisateur veut un modal plus simple (toast-style), préférer le
 *   composant `Toast` (à venir).
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE.
 *
 * Contraintes accessibilité (WCAG 2.1 AA) :
 *   - Toujours fournir un `title` (sinon `aria-labelledby` cassé).
 *   - Le bouton de fermeture (X) doit avoir un libellé accessible.
 *
 * @author Mika
 * @since 2026-05-04
 */

import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from './Button';
import styles from './Modal.module.css';

export interface ModalProps {
  /** Affiche / cache le modal. */
  open: boolean;
  /** Callback déclenché à la fermeture (ESC, overlay, bouton X). */
  onClose: () => void;
  /** Titre de la modale (obligatoire, lu par les lecteurs d'écran). */
  title: ReactNode;
  /** Contenu principal. */
  children?: ReactNode;
  /** Pied, boutons d'action (annuler / valider). */
  footer?: ReactNode;
  /** Si true, ferme la modale au clic sur l'overlay (défaut true). */
  closeOnOverlayClick?: boolean;
  /** Largeur maximale (`sm` = 360, `md` = 520, `lg` = 720). Défaut `md`. */
  size?: 'sm' | 'md' | 'lg';
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  closeOnOverlayClick = true,
  size = 'md',
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /**
   * Chantier J (accessibilité) : WCAG 4.1.1 / 4.1.2, RGAA 8.9.
   *
   * L'identifiant du titre était la constante « tne-modal-title ». Dès que
   * DEUX Modal coexistent dans le DOM (et il suffit qu'une modale fermée
   * reste montée, ce que fait `<dialog>`), le document contient deux fois le
   * même `id` : `aria-labelledby` retombe sur le PREMIER trouvé, et la modale
   * ouverte s'annonce avec le titre d'une autre. `useId()` rend l'identifiant
   * unique par instance sans rien changer à l'API du composant.
   */
  const titreId = useId();

  // Synchronise l'état React `open` avec l'API native du <dialog>.
  // showModal() = focus trap + inertness ; show() = juste affichage.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;

    if (open && !dlg.open) {
      dlg.showModal();
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Pont entre l'événement natif `cancel` (ESC) et le callback React.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;

    const handleCancel = (e: Event) => {
      e.preventDefault(); // empêche fermeture instantanée, onClose gère.
      onClose();
    };

    dlg.addEventListener('cancel', handleCancel);
    return () => dlg.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  // Fermeture par clic sur le backdrop (overlay), détectée via un
  // listener attaché au DOM natif (et non pas onClick JSX), ce qui évite
  // les warnings jsx-a11y/no-noninteractive-element-interactions sur le
  // <dialog> tout en gardant le focus trap et l'Escape natif.
  // FIX 2026-05-06 (S18-A) : refactor pour passer la lint a11y.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg || !closeOnOverlayClick) return;
    const handleBackdropClick = (e: MouseEvent) => {
      // L'événement sur le <dialog> dont la cible est le <dialog> lui-même
      // = clic sur le backdrop (le contenu interne aurait une cible enfant).
      if (e.target === dlg) {
        onClose();
      }
    };
    dlg.addEventListener('click', handleBackdropClick);
    return () => dlg.removeEventListener('click', handleBackdropClick);
  }, [closeOnOverlayClick, onClose]);

  return (
    <dialog
      ref={dialogRef}
      className={[styles.dialog, styles[`size_${size}`]].join(' ')}
      aria-labelledby={titreId}
    >
      <header className={styles.header}>
        <h2 id={titreId} className={styles.title}>
          {title}
        </h2>
        <Button
          variant="tertiary"
          size="sm"
          onClick={onClose}
          aria-label={typeof title === 'string' ? `Fermer « ${title} »` : 'Fermer la fenêtre'}
        >
          <FormattedMessage id="btn.close" />
        </Button>
      </header>

      <div className={styles.body}>{children}</div>

      {footer && <footer className={styles.footer}>{footer}</footer>}
    </dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Polyfill <dialog> pour anciens navigateurs (négligeable en 2026 mais
//    si on rétro-porte vers NOLAS qui ciblait IE11, faudra changer).
//  - Variante "drawer" (slide depuis le bas, façon mobile native).
// Notes pour rétro-port NOLAS :
//  - NOLAS a des modals jQuery Materialize. Ce composant peut les remplacer
//    en JS pur via dialog.showModal(), voir polyfill GoogleChromeLabs.
// ──────────────────────────────────────────────────────────────────────────
